<script lang="ts">
  import { AlertTriangle, BarChart3, Loader2 } from 'lucide-svelte';
  import { onMount } from 'svelte';
  import { type WeeklyBreakdownResponse, fetchWeeklyBreakdown } from '$lib/api/sweeps';
  import type { ApiError } from '$lib/api/client';
  import { Button } from '$lib/components/ui/button';
  import { Dialog, DialogContent, DialogTitle } from '$lib/components/ui/dialog';
  import CopyableSessionId from './CopyableSessionId.svelte';

  // ============================================================
  // <WeeklyBreakdownModal sweepId={...} open={...} onClose={...} />
  //
  // Lazy-fetch del weekly breakdown del sweep cuando se abre. Render
  // como tabla luxury (no terminal monoespaciado de v1): cada semana
  // es una seccion con eyebrow + tabla de days + totales en color.
  //
  // Estados: loading / data / not_found / empty / invalid_shape / error.
  // ============================================================

  interface Props {
    sweepId: string | null;
    open: boolean;
    onClose: () => void;
  }

  let { sweepId, open = $bindable(), onClose }: Props = $props();

  let data = $state<WeeklyBreakdownResponse | null>(null);
  let loading = $state(false);
  let error = $state<{ status?: number; message: string } | null>(null);
  let lastFetchedSweepId = $state<string | null>(null);

  async function load(id: string): Promise<void> {
    loading = true;
    error = null;
    data = null;
    try {
      data = await fetchWeeklyBreakdown(id);
      lastFetchedSweepId = id;
    } catch (err) {
      const apiErr = err as ApiError;
      const body = (apiErr.body ?? {}) as { error?: string; reason?: string };
      const reason = body.reason ?? body.error ?? apiErr.message;
      error = { status: apiErr.status, message: reason };
    } finally {
      loading = false;
    }
  }

  // Refetch cuando cambia sweepId con el modal abierto, o cuando se abre
  // por primera vez. NO refetcheamos si abrimos el mismo sweepId dos veces
  // seguidas (cache trivial in-memory).
  $effect(() => {
    if (!open || !sweepId) return;
    if (lastFetchedSweepId === sweepId && data) return;
    void load(sweepId);
  });

  onMount(() => {
    if (open && sweepId) void load(sweepId);
  });

  // ----- Formatters -----
  function fmtPnL(n: number): string {
    if (n === 0) return '$0.00';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }

  function fmtPF(v: number | 'Infinity'): string {
    if (v === 'Infinity') return '∞';
    if (!Number.isFinite(v)) return '—';
    return v.toFixed(2);
  }

  function fmtPct(n: number): string {
    return `${n.toFixed(1)}%`;
  }

  function fmtMin(n: number): string {
    return `${n.toFixed(1)}m`;
  }

  function pnlClass(n: number): string {
    if (n > 0) return 'text-success';
    if (n < 0) return 'text-destructive';
    return 'text-muted-foreground';
  }

  function fmtWeekRange(start: string, end: string): string {
    if (!start && !end) return '';
    if (start === end) return start;
    return `${start} → ${end}`;
  }

  // Title de error para 404 vs 400 vs otros.
  let errorKind = $derived.by(() => {
    if (!error) return null;
    if (error.status === 404) return 'not_found';
    if (error.status === 400) return 'invalid_shape';
    return 'unknown';
  });
</script>

<Dialog bind:open onOpenChange={(o) => !o && onClose()}>
  <DialogContent class="max-h-[90vh] max-w-4xl overflow-y-auto">
    <div class="flex flex-wrap items-center justify-between gap-3 pr-8">
      <DialogTitle class="flex flex-wrap items-center gap-2.5">
        <BarChart3 class="text-gold size-5" />
        <span class="text-gold text-display text-2xl">Weekly Breakdown</span>
        {#if sweepId}
          <CopyableSessionId sessionId={sweepId} ariaLabel="Copy sweep ID" stopPropagation={false} />
        {/if}
      </DialogTitle>
    </div>

    <div class="mt-4">
      {#if loading}
        <div class="text-muted-foreground flex items-center justify-center gap-3 py-16">
          <Loader2 class="size-5 animate-spin" />
          <span class="text-sm">Loading breakdown…</span>
        </div>
      {:else if error}
        <div
          role="alert"
          class="bg-destructive/10 border-destructive/40 flex items-start gap-3 rounded-lg border p-4"
        >
          <AlertTriangle class="text-destructive mt-0.5 size-5 shrink-0" />
          <div class="flex-1 text-sm">
            <div class="text-destructive font-medium">
              {#if errorKind === 'not_found'}
                Sweep not found
              {:else if errorKind === 'invalid_shape'}
                Malformed weekly breakdown data
              {:else}
                Error loading breakdown
              {/if}
            </div>
            <div class="text-muted-foreground mt-1">{error.message}</div>
          </div>
        </div>
      {:else if data && data.weeks.length > 0}
        <div class="space-y-6">
          {#each data.weeks as week (week.weekNumber)}
            <section class="bg-card-luxury lifted-edge border-border/70 rounded-lg border">
              <!-- Week header: eyebrow + title -->
              <header class="border-border/60 flex flex-wrap items-baseline justify-between gap-2 border-b px-5 py-3">
                <div class="flex flex-col gap-0.5">
                  <span class="text-eyebrow">Week {week.weekNumber}</span>
                  <span class="text-foreground font-mono text-sm tabular-nums">
                    {fmtWeekRange(week.weekStart, week.weekEnd)} ET
                  </span>
                </div>
                <div class="flex items-baseline gap-4 text-sm">
                  <div class="flex items-baseline gap-1.5">
                    <span class="text-muted-foreground text-xs">Trades</span>
                    <span class="font-mono font-semibold tabular-nums">{week.trades}</span>
                  </div>
                  <div class="flex items-baseline gap-1.5">
                    <span class="text-muted-foreground text-xs">Total</span>
                    <span class={['font-mono font-semibold tabular-nums', pnlClass(week.totalPnL)].join(' ')}>
                      {fmtPnL(week.totalPnL)}
                    </span>
                  </div>
                </div>
              </header>

              <!-- Days table -->
              {#if week.dailyBreakdown.length > 0}
                <div class="overflow-x-auto">
                  <table class="w-full text-sm">
                    <thead class="bg-surface-2/50 border-border border-b">
                      <tr>
                        <th class="text-eyebrow text-gold/80 px-4 py-2 text-left">Day</th>
                        <th class="text-eyebrow text-gold/80 px-3 py-2 text-right">Trades</th>
                        <th class="text-eyebrow text-gold/80 px-3 py-2 text-right">P&L</th>
                        <th class="text-eyebrow text-gold/80 px-3 py-2 text-right">WR</th>
                        <th class="text-eyebrow text-gold/80 px-3 py-2 text-right">PF</th>
                        <th class="text-eyebrow text-gold/80 px-3 py-2 text-right">Win Dur</th>
                        <th class="text-eyebrow text-gold/80 px-3 py-2 text-right">Loss Dur</th>
                      </tr>
                    </thead>
                    <tbody>
                      {#each week.dailyBreakdown as day, idx (`${week.weekNumber}-${day.dayName}-${idx}`)}
                        <tr class="border-border/40 hover:bg-surface-2/40 border-b transition-colors last:border-0">
                          <td class="text-foreground px-4 py-2 font-medium">{day.dayName}</td>
                          <td class="px-3 py-2 text-right font-mono tabular-nums">{day.trades}</td>
                          <td class={['px-3 py-2 text-right font-mono font-medium tabular-nums', pnlClass(day.totalPnL)].join(' ')}>
                            {fmtPnL(day.totalPnL)}
                          </td>
                          <td class="text-muted-foreground px-3 py-2 text-right font-mono tabular-nums">{fmtPct(day.winRate)}</td>
                          <td class="text-muted-foreground px-3 py-2 text-right font-mono tabular-nums">{fmtPF(day.profitFactor)}</td>
                          <td class="text-muted-foreground px-3 py-2 text-right font-mono tabular-nums">{fmtMin(day.avgWinDurationMin)}</td>
                          <td class="text-muted-foreground px-3 py-2 text-right font-mono tabular-nums">{fmtMin(day.avgLossDurationMin)}</td>
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              {/if}

              <!-- Week totals footer -->
              <footer class="border-border/60 grid grid-cols-2 gap-x-6 gap-y-2 border-t px-5 py-3 sm:grid-cols-4 md:grid-cols-6">
                <div>
                  <div class="text-eyebrow">WR</div>
                  <div class="font-mono text-sm font-semibold tabular-nums">{fmtPct(week.winRate)}</div>
                </div>
                <div>
                  <div class="text-eyebrow">PF</div>
                  <div class="font-mono text-sm font-semibold tabular-nums">{fmtPF(week.profitFactor)}</div>
                </div>
                <div>
                  <div class="text-eyebrow">Win Dur</div>
                  <div class="font-mono text-sm font-semibold tabular-nums">{fmtMin(week.avgWinDurationMin)}</div>
                </div>
                <div>
                  <div class="text-eyebrow">Loss Dur</div>
                  <div class="font-mono text-sm font-semibold tabular-nums">{fmtMin(week.avgLossDurationMin)}</div>
                </div>
                <div>
                  <div class="text-eyebrow">Long P&L</div>
                  <div class={['font-mono text-sm font-semibold tabular-nums', pnlClass(week.longPnL)].join(' ')}>
                    {fmtPnL(week.longPnL)}
                  </div>
                </div>
                <div>
                  <div class="text-eyebrow">Short P&L</div>
                  <div class={['font-mono text-sm font-semibold tabular-nums', pnlClass(week.shortPnL)].join(' ')}>
                    {fmtPnL(week.shortPnL)}
                  </div>
                </div>
              </footer>
            </section>
          {/each}
        </div>
      {:else if data}
        <div class="text-muted-foreground flex items-center justify-center py-16 text-sm italic">
          No weekly breakdown available for this sweep.
        </div>
      {/if}
    </div>

    <div class="mt-2 flex justify-end">
      <Button variant="secondary" size="sm" onclick={onClose}>Close</Button>
    </div>
  </DialogContent>
</Dialog>
